import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useAnalyticsMetadata } from './AnalyticsMetadata';
import { BaseAnalyticsEvent, UnknownObject } from './types';

interface AnalyticsContextValue {
  onEvent: (event: BaseAnalyticsEvent, metadata: UnknownObject) => void;
}

export const AnalyticsContext = createContext<AnalyticsContextValue>({
  onEvent: () => {},
});

interface AnalyticsHook<T> {
  trackEvent: (event: T) => void;
}

export const useAnalytics = <T extends BaseAnalyticsEvent>(): AnalyticsHook<T> => {
  const getMetadata = useAnalyticsMetadata();
  const { onEvent } = useContext(AnalyticsContext);

  const trackEvent: AnalyticsHook<T>['trackEvent'] = useCallback(
    (analyticsEvent) => {
      onEvent(analyticsEvent, getMetadata());
    },
    [onEvent, getMetadata]
  );

  return useMemo(
    () => ({
      trackEvent,
    }),
    [trackEvent]
  );
};

interface AnalyticsProviderProps extends AnalyticsContextValue {
  children: React.ReactNode;
  isEnabled: boolean;
}

export const AnalyticsContextProvider = ({
  onEvent,
  isEnabled,
  children,
}: AnalyticsProviderProps) => {
  const isEnabledRef = useRef(isEnabled);

  useEffect(() => {
    isEnabledRef.current = isEnabled;
  }, [isEnabled]);

  const delayedEvents = useRef<[BaseAnalyticsEvent, UnknownObject][]>([]);
  const contextValue = useMemo(
    (): AnalyticsContextValue => ({
      onEvent: (event, metadata) => {
        if (isEnabledRef.current) {
          onEvent(event, metadata);
        } else {
          delayedEvents.current.push([event, metadata]);
        }
      },
    }),
    [onEvent]
  );

  useEffect(() => {
    if (isEnabled && delayedEvents.current.length > 0) {
      const eventsToSend = delayedEvents.current;

      delayedEvents.current = [];

      eventsToSend.forEach(([event, metadata]) => {
        onEvent(event, metadata);
      });
    }
  }, [isEnabled, onEvent]);

  return <AnalyticsContext.Provider value={contextValue}>{children}</AnalyticsContext.Provider>;
};
